import { useCallback, useEffect, useRef, useState } from 'react'
import { AuthCallbackPage } from '../auth/AuthCallbackPage'
import { useAuth } from '../auth/AuthContext'
import { AuthProvider } from '../auth/AuthProvider'
import { AUTH_CALLBACK_PATH } from '../auth/config'
import { Header } from '../components/Header'
import { CodingStudioPanels } from '../components/CodingPanels'
import { Inspector } from '../components/Inspector'
import { LandPortal, MissionHub, WorldPreview } from '../components/LandPortal'
import { PartsLibrary } from '../components/PartsLibrary'
import { Workspace } from '../components/Workspace'
import { MissionSidebar, SimulationSidebar } from '../components/StudioPanels'
import { APP_VERSION } from '../config/app-version'
import { starterAssembly } from '../domain/assembly'
import { normalizeConnectionJointTypes } from '../domain/connection'
import { useLandProgress } from '../land/LandProgressContext'
import { LandProgressProvider } from '../land/LandProgressProvider'
import {
  DEFAULT_CODING_PROGRAM_NAME,
  createDefaultCodingProgram,
  normalizeCodingProgram,
} from '../domain/coding'
import {
  workspaceDraftRepository,
  type WorkspaceDraft,
  type WorkspaceDraftId,
} from '../services/workspaceDraftRepository'
import { useStudioStore } from '../store/useStudioStore'

export type AppScreen = 'portal' | 'autonomous' | 'mission-hub' | 'mission-studio' | 'world'

const APP_SCREENS = new Set<AppScreen>(['portal', 'autonomous', 'mission-hub', 'mission-studio', 'world'])
const HISTORY_STATE_KEY = 'pingponglandScreen'

type AppHistoryState = {
  [HISTORY_STATE_KEY]: AppScreen
  hasPortalEntry?: boolean
}

function appHistoryState(): AppHistoryState | null {
  const state = window.history.state as Partial<AppHistoryState> | null
  return state && APP_SCREENS.has(state[HISTORY_STATE_KEY] as AppScreen)
    ? state as AppHistoryState
    : null
}

function historyState(screen: AppScreen, hasPortalEntry = false): AppHistoryState {
  return { [HISTORY_STATE_KEY]: screen, hasPortalEntry }
}

function currentDraft(): WorkspaceDraft {
  const state = useStudioStore.getState()
  return {
    parts: state.placedParts,
    connections: state.connections,
    robotGroups: state.robotGroups,
    motorControls: state.motorControls,
    simulationObstacles: state.simulationObstacles,
    codingProgramName: state.codingProgramName,
    codingProgram: state.codingProgram,
    environment: state.environment,
  }
}

function saveActiveDraft() {
  try {
    workspaceDraftRepository.save(workspaceDraftRepository.active(), currentDraft())
  } catch {
    // The main studio session still protects the current work if draft storage is unavailable.
  }
}

function defaultDraft(target: WorkspaceDraftId): WorkspaceDraft {
  return {
    parts: starterAssembly,
    connections: [],
    robotGroups: [],
    motorControls: {},
    simulationObstacles: [],
    codingProgramName: DEFAULT_CODING_PROGRAM_NAME,
    codingProgram: createDefaultCodingProgram(),
    environment: target === 'mission-01' ? 'obstacle' : 'flat',
  }
}

function openDraft(target: WorkspaceDraftId) {
  const current = workspaceDraftRepository.active()
  if (current !== target) {
    saveActiveDraft()
    const draft = workspaceDraftRepository.load(target) ?? defaultDraft(target)
    const connections = normalizeConnectionJointTypes(draft.connections)
    useStudioStore.setState({
      mode: 'assembly',
      environment: draft.environment,
      placedParts: draft.parts,
      connections,
      robotGroups: draft.robotGroups,
      motorControls: draft.motorControls,
      simulationObstacles: draft.simulationObstacles,
      codingProgramName: draft.codingProgramName,
      codingProgram: normalizeCodingProgram(draft.codingProgram),
      selectedInstanceId: draft.parts[0]?.instanceId ?? null,
      selectedInstanceIds: draft.parts[0] ? [draft.parts[0].instanceId] : [],
      isSimulating: false,
      history: [],
      future: [],
      notice: null,
    })
  }
  useStudioStore.getState().setPartLimit(target === 'mission-01' ? 8 : null)
  workspaceDraftRepository.setActive(target)
}

function isAuthCallbackRoute() {
  return window.location.pathname.replace(/\/+$/, '') === AUTH_CALLBACK_PATH
}

export function App({ initialScreen }: { initialScreen?: AppScreen }) {
  const [showAuthCallback, setShowAuthCallback] = useState(isAuthCallbackRoute)
  const continueFromAuth = useCallback(() => {
    window.history.replaceState(null, '', '/')
    setShowAuthCallback(false)
  }, [])

  return (
    <AuthProvider>
      {showAuthCallback ? (
        <AuthCallbackPage onContinue={continueFromAuth} />
      ) : (
        <LandProgressProvider>
          <LandApplication initialScreen={initialScreen} />
          <AuthAvailabilityNotice />
          <LandProgressNotice />
        </LandProgressProvider>
      )}
    </AuthProvider>
  )
}

function LandApplication({ initialScreen }: { initialScreen?: AppScreen }) {
  const [screen, setScreen] = useState<AppScreen>(() => initialScreen ?? 'portal')
  const screenRef = useRef(screen)
  const missionStartRequestedRef = useRef(false)
  const { isAuthenticated } = useAuth()
  const { startMission: syncMissionStart } = useLandProgress()
  const mode = useStudioStore((state) => state.mode)
  const studioExperience = screen === 'mission-studio' ? 'mission' : 'autonomous'

  screenRef.current = screen

  useEffect(() => {
    const currentState = appHistoryState()
    if (screenRef.current === 'portal') {
      window.history.replaceState(historyState('portal'), '')
    } else if (!currentState) {
      window.history.replaceState(historyState('portal'), '')
      window.history.pushState(historyState(screenRef.current, true), '')
    }

    const handlePopState = (event: PopStateEvent) => {
      const target = (event.state as Partial<AppHistoryState> | null)?.[HISTORY_STATE_KEY]
      if (!target || !APP_SCREENS.has(target as AppScreen)) return
      if (screenRef.current === 'autonomous' || screenRef.current === 'mission-studio') saveActiveDraft()
      setScreen(target as AppScreen)
    }
    window.addEventListener('popstate', handlePopState)
    return () => window.removeEventListener('popstate', handlePopState)
  }, [])

  useEffect(() => {
    if (screen !== 'mission-studio' || !isAuthenticated) {
      missionStartRequestedRef.current = false
      return
    }
    if (missionStartRequestedRef.current) return

    missionStartRequestedRef.current = true
    void syncMissionStart('LAND_BASIC_01')
  }, [isAuthenticated, screen, syncMissionStart])

  const goPortal = () => {
    if (screen === 'autonomous' || screen === 'mission-studio') saveActiveDraft()
    setScreen('portal')
    if (appHistoryState()?.hasPortalEntry) window.history.back()
    else window.history.replaceState(historyState('portal'), '')
  }
  const enterActivity = (target: AppScreen) => {
    window.history.pushState(historyState(target, true), '')
    setScreen(target)
  }
  const replaceActivity = (target: AppScreen) => {
    window.history.replaceState(historyState(target, true), '')
    setScreen(target)
  }
  const openAutonomous = () => {
    openDraft('autonomous')
    enterActivity('autonomous')
  }
  const openMission = () => enterActivity('mission-hub')
  const startMission = () => {
    openDraft('mission-01')
    replaceActivity('mission-studio')
  }
  const openMissionHub = () => {
    if (screen === 'mission-studio') saveActiveDraft()
    replaceActivity('mission-hub')
  }
  const openWorld = () => enterActivity('world')

  if (screen === 'portal') {
    return (
      <main className="portal-shell">
        <LandPortal onAutonomous={openAutonomous} onMission={openMission} onWorld={openWorld} />
        <AppVersion />
      </main>
    )
  }

  if (screen === 'mission-hub') {
    return (
      <main className="portal-shell">
        <MissionHub onHome={goPortal} onStart={startMission} />
        <AppVersion />
      </main>
    )
  }

  if (screen === 'world') {
    return (
      <main className="portal-shell">
        <WorldPreview onHome={goPortal} />
        <AppVersion />
      </main>
    )
  }

  return (
    <main className={`app-shell app-shell-${studioExperience}`}>
      <Header
        experience={studioExperience}
        onPortal={goPortal}
        onMissionHub={openMissionHub}
      />
      <section className={`studio studio-${mode} studio-experience-${studioExperience}`}>
        {mode === 'coding' ? <CodingStudioPanels /> : (
          <>
            {mode === 'assembly' && <PartsLibrary showCompletedRobots={studioExperience === 'autonomous'} />}
            {mode === 'simulation' && <MissionSidebar experience={studioExperience} />}
            <Workspace />
            {mode === 'assembly' && <Inspector />}
            {mode === 'simulation' && <SimulationSidebar />}
          </>
        )}
      </section>
      <AppVersion />
    </main>
  )
}

function AuthAvailabilityNotice() {
  const { status, authError, refreshSession } = useAuth()
  if (status !== 'unavailable' || !authError) return null

  return (
    <aside className="service-notice" role="status">
      <span>{authError} 로그인 없이도 모든 기능을 계속 사용할 수 있습니다.</span>
      <button type="button" onClick={() => void refreshSession()}>다시 확인</button>
    </aside>
  )
}

function LandProgressNotice() {
  const { error, clearError } = useLandProgress()
  if (!error) return null

  return (
    <aside className="service-notice service-notice-progress" role="status">
      <span>{error.message}</span>
      <button type="button" onClick={clearError}>확인</button>
    </aside>
  )
}

function AppVersion() {
  return (
    <span className="app-version" aria-label={`핑퐁랜드 버전 ${APP_VERSION}`}>
      v{APP_VERSION}
    </span>
  )
}
